import { BANKROLL_PRESETS } from "../lib/bankroll";
import type { BankrollTier } from "../lib/types";

interface BankrollPickerProps {
	tier: BankrollTier;
	// Raw custom-amount input — only meaningful when tier === "custom".
	customUsd: string;
	onTierChange: (tier: BankrollTier) => void;
	onCustomChange: (value: string) => void;
	// Free accounts may only pick the $10K tier; the rest render disabled
	// with an upgrade hint.
	isPaid: boolean;
	disabled?: boolean;
}

// BankrollPicker — the four-option ($10K / $100K / $1M / Custom) bankroll
// selector shared by the Create Fam + Create Fantasy Fam modals. The DB
// trigger enforce_free_bankroll_tier is the real gate; this is the UX layer
// that surfaces the paid-only tiers up front.
export function BankrollPicker({
	tier,
	customUsd,
	onTierChange,
	onCustomChange,
	isPaid,
	disabled = false,
}: BankrollPickerProps) {
	return (
		<fieldset className="space-y-2">
			<legend className="text-sm font-medium">Bankroll</legend>
			<div className="grid gap-2 sm:grid-cols-2">
				{BANKROLL_PRESETS.map((preset) => {
					const locked = preset.paidOnly && !isPaid;
					const selected = tier === preset.tier;
					return (
						<label
							key={preset.tier}
							className={`flex flex-col gap-1 rounded-md border p-3 text-left text-sm transition ${
								selected
									? "border-[var(--color-accent)] bg-[var(--color-bg-elev)]"
									: "border-[var(--color-border)]"
							} ${
								locked
									? "cursor-not-allowed opacity-50"
									: "cursor-pointer hover:border-[var(--color-accent)]"
							}`}
						>
							<div className="flex items-center gap-2">
								<input
									type="radio"
									name="bankroll"
									value={preset.tier}
									checked={selected}
									onChange={() => onTierChange(preset.tier)}
									disabled={disabled || locked}
									className="h-4 w-4 accent-[var(--color-accent)]"
								/>
								<span className="font-semibold">{preset.label}</span>
							</div>
							<span className="ml-6 text-xs text-[var(--color-muted)]">
								{locked
									? "Paid plan only — upgrade to unlock."
									: preset.blurb}
							</span>
						</label>
					);
				})}
			</div>

			{tier === "custom" && (
				<label className="block space-y-1">
					<span className="text-xs font-medium">Custom bankroll (USD)</span>
					<input
						type="number"
						value={customUsd}
						onChange={(e) => onCustomChange(e.target.value)}
						placeholder="250000"
						min="1"
						step="any"
						disabled={disabled}
						className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-2 text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
					/>
				</label>
			)}

			{!isPaid && (
				<p className="text-xs text-[var(--color-muted)]">
					Free tier runs at the $10K bankroll. Upgrade for $100K, $1M, or a
					custom size.
				</p>
			)}
		</fieldset>
	);
}
